import { RefObject, useEffect } from 'react';

type RefObjectType<T extends Element = Element> = RefObject<
  T | null | undefined
>;

/**
 * A react hook that fires the callback when clicking outside of the referenced element.
 * @param {Object} ref - React reference object with a `current` property.
 * @param {Function} callback - A callback function that fires when clicking outside the element.
 */
// Overload 1: generic same-element ref(s)
function useClickOutside<T extends Element = Element>(
  ref: RefObjectType<T> | RefObjectType<T>[],
  callback: (e: MouseEvent) => void,
): void;

// Overload 2: mixed element refs (array of Element refs)
function useClickOutside(
  ref:
    | RefObject<Element | null | undefined>
    | RefObject<Element | null | undefined>[],
  callback: (e: MouseEvent) => void,
): void;

// Implementation
/**
 * Implementation of useClickOutside overloads.
 * @param ref - Single React ref or array of refs to monitor for outside clicks.
 * @param callback - Callback invoked when a click occurs outside all provided refs.
 */
function useClickOutside(
  ref:
    | RefObject<Element | null | undefined>
    | RefObject<Element | null | undefined>[],
  callback: (e: MouseEvent) => void,
): void {
  const handleClick = (e: MouseEvent): void => {
    const refs = Array.isArray(ref) ? ref : [ref];
    if (
      refs.every(
        (ref: RefObject<Element | null | undefined>) =>
          ref.current &&
          ref.current instanceof Element &&
          !ref.current.contains(e.target as Element),
      )
    ) {
      callback(e);
    }
  };

  useEffect(() => {
    document.body.addEventListener('click', handleClick);
    return () => {
      // Cleanup
      document.body.removeEventListener('click', handleClick);
    };
  }, [ref, callback]);
}

export default useClickOutside;
